import React from 'react';
import { Tooltip } from '@fluentui/react-components';
import { HeartFilled, HeartRegular } from '@fluentui/react-icons';
import { IProduct, ISearchContext } from '../../Api/Models/Products';
import { useFavorites } from '../Helpers/useFavorites';
import { useLocalization } from '../Helpers/useLocalization';

interface IFavoriteButtonProps {
    product: IProduct;
    // Extra class for placement (e.g. the card's overlay position). The button's own look comes from
    // .sf-favorite-btn.
    className?: string;
    style?: React.CSSProperties;
    // Interest-signal context: the surface this button is rendered on (search/section/product/cart/…)
    // and, when the surface is search, the active search criteria. Forwarded to the favorite toggle so
    // the BFF can capture them onto the interest signal.
    source?: string;
    searchContext?: ISearchContext;
}

// The heart. Renders nothing at all when favorites are off for the storefront or the shopper is a
// guest — see useFavorites().enabled — so no surface has to repeat that check.
const FavoriteButton: React.FC<IFavoriteButtonProps> = (props) => {
    const L = useLocalization();
    const { enabled, isFavorite, toggle } = useFavorites();
    if (!enabled) return null;

    const favorite = isFavorite(props.product.id, props.product.isFavorite);
    const label = favorite ? L('localization_favorite_remove') : L('localization_favorite_add');

    return (
        <Tooltip content={label} relationship="label">
            <button
                type="button"
                aria-label={label}
                aria-pressed={favorite}
                className={`sf-favorite-btn${favorite ? ' sf-favorite-btn-on' : ''}${props.className ? ` ${props.className}` : ''}`}
                style={props.style}
                // Cards navigate on click, so the heart must not bubble.
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggle(props.product.id, favorite, props.source, props.searchContext); }}
            >
                {favorite ? <HeartFilled /> : <HeartRegular />}
            </button>
        </Tooltip>
    );
};

export default FavoriteButton;
